import {
  Box,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import * as Sentry from '@sentry/react';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IcomxResourceDescriptor,
  useEnsureIcomxResourceLink,
  useIcomxEmbedConfig,
} from 'src/services/api/icomx';
import useNotiMessage from 'src/hooks/useNotiMessage';
import useObjectState from 'src/hooks/useObjectState';
import { getIcomxApiErrorMessage, getIcomxBridgeErrorMessage } from './error-utils';
import useIcomxBridge from './useIcomxBridge';
import { IcomxChatContext } from './IcomxChatContext';
import { IcomxSecurityRejection } from './types';

const ICOMX_IFRAME_SANDBOX = 'allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox';
const OPEN_CHAT_CONVERSATION = 'comx.host.open_last_conversation' as const;
export const ICOMX_EXPANDED_WIDTH = '50vw';
export const ICOMX_MINIMIZED_WIDTH = '15vw';
export const ICOMX_EXPANDED_HEIGHT = '70vh';
const ICOMX_VIEWPORT_MARGIN = 24;
const ICOMX_HEADER_HEIGHT = 56;
const ICOMX_MIN_WIDTH = 220;
const ICOMX_MIN_EXPANDED_WIDTH = 320;
const ICOMX_MIN_EXPANDED_BODY_HEIGHT = 280;
const ICOMX_DRAG_THRESHOLD = 4;
const ICOMX_DIRECTION_SWITCH_THRESHOLD = 96;
const ICOMX_POSITION_STORAGE_KEY = 'icomx-widget-position';
const MISSING_CONTACT_UUID_MESSAGE = 'iComx did not return a resource chat mapping. Please try again.';
const ICOMX_READY_TIMEOUT_MS = 15000;
const ICOMX_READY_TIMEOUT_MESSAGE = 'iComx chat did not finish loading. Please try again after the chat finishes booting or signing in.';
const ICOMX_DRAG_CLICK_SUPPRESSION_MS = 120;

type ViewportBucket = 'mobile' | 'tablet' | 'desktop';

interface IcomxChatProviderProps {
  children: ReactNode;
}

interface WidgetState {
  isExpanded: boolean;
  activeIntent: typeof OPEN_CHAT_CONVERSATION | null;
  pendingContactUuid: string | null;
  openDirection: 'up' | 'down';
}

interface ViewportSize {
  width: number;
  height: number;
}

interface HeaderPosition {
  left: number;
  top: number;
}

interface WidgetLayout {
  headerLeft: number;
  headerTop: number;
  panelTop: number;
  width: number;
  height: number;
  openDirection: 'up' | 'down';
}

interface DragState {
  pointerId: number;
  originLeft: number;
  originTop: number;
  originX: number;
  originY: number;
  moved: boolean;
}

interface DragListeners {
  move: (event: PointerEvent) => void;
  up: (event: PointerEvent) => void;
  cancel: (event: PointerEvent) => void;
}

interface StoredHeaderPosition {
  left: number;
  top: number;
}

interface StoredHeaderPositions {
  mobile?: StoredHeaderPosition;
  tablet?: StoredHeaderPosition;
  desktop?: StoredHeaderPosition;
}

export function resolveIcomxIframeOrigin(
  iframeOrigin?: string | null,
  iframeUrl?: string | null
): string | null {
  const candidates = [iframeOrigin, iframeUrl];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    try {
      const normalizedUrl = new URL(candidate);

      if (normalizedUrl.origin && normalizedUrl.origin !== 'null') {
        return normalizedUrl.origin;
      }
    } catch (error) {
      if (typeof candidate === 'string' && /^https?:\/\//i.test(candidate)) {
        const trimmedCandidate = candidate.replace(/\/+$/, '');

        if (trimmedCandidate) {
          return trimmedCandidate;
        }
      }
    }
  }

  return null;
}

export function buildIcomxIframeSrc(iframeUrl?: string | null): string | null {
  if (!iframeUrl) {
    return null;
  }

  try {
    const resolvedUrl = new URL(iframeUrl);

    if (typeof window !== 'undefined' && window.location.origin) {
      resolvedUrl.searchParams.set('hostOrigin', window.location.origin);
    }

    return resolvedUrl.toString();
  } catch (error) {
    return iframeUrl;
  }
}

function clamp(value: number, min: number, max: number) {
  if (max < min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
}

function resolveViewportSize(): ViewportSize {
  if (typeof window === 'undefined') {
    return { width: 1280, height: 720 };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

function getDefaultHeaderPosition(viewport: ViewportSize): HeaderPosition {
  return {
    left: ICOMX_VIEWPORT_MARGIN,
    top: viewport.height - ICOMX_HEADER_HEIGHT - ICOMX_VIEWPORT_MARGIN,
  };
}

export function getViewportBucket(viewport: ViewportSize): ViewportBucket {
  if (viewport.width < 768) {
    return 'mobile';
  }

  if (viewport.width < 1280) {
    return 'tablet';
  }

  return 'desktop';
}

function readStoredHeaderPositions(): StoredHeaderPositions {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(ICOMX_POSITION_STORAGE_KEY);

    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue) as StoredHeaderPositions | Partial<StoredHeaderPosition>;

    if ('left' in parsedValue || 'top' in parsedValue) {
      if (typeof parsedValue.left === 'number' && typeof parsedValue.top === 'number') {
        return {
          desktop: {
            left: parsedValue.left,
            top: parsedValue.top,
          },
        };
      }

      return {};
    }

    return parsedValue as StoredHeaderPositions;
  } catch (error) {
    console.warn('Unable to read stored iComx widget position', error);
    return {};
  }
}

function readStoredHeaderPosition(bucket: ViewportBucket): StoredHeaderPosition | null {
  const storedPositions = readStoredHeaderPositions();
  const storedPosition = storedPositions[bucket];

  if (!storedPosition) {
    return null;
  }

  if (typeof storedPosition.left !== 'number' || typeof storedPosition.top !== 'number') {
    return null;
  }

  return storedPosition;
}

function writeStoredHeaderPosition(position: HeaderPosition, bucket: ViewportBucket) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const storedPositions = readStoredHeaderPositions();

    window.localStorage.setItem(ICOMX_POSITION_STORAGE_KEY, JSON.stringify({
      ...storedPositions,
      [bucket]: position,
    }));
  } catch (error) {
    console.warn('Unable to persist iComx widget position', error);
  }
}

function getViewportUnits(value: string, viewport: number) {
  if (value.endsWith('vw') || value.endsWith('vh')) {
    return (Number.parseFloat(value) / 100) * viewport;
  }

  return Number.parseFloat(value);
}

export function getWidgetLayout(
  position: HeaderPosition,
  viewport: ViewportSize,
  isExpanded: boolean,
  openDirection: 'up' | 'down'
): WidgetLayout {
  const availableViewportWidth = Math.max(viewport.width - ICOMX_VIEWPORT_MARGIN * 2, ICOMX_MIN_WIDTH);
  const minimizedWidth = clamp(
    getViewportUnits(ICOMX_MINIMIZED_WIDTH, viewport.width),
    ICOMX_MIN_WIDTH,
    availableViewportWidth
  );
  const expandedWidth = clamp(
    getViewportUnits(ICOMX_EXPANDED_WIDTH, viewport.width),
    Math.min(ICOMX_MIN_EXPANDED_WIDTH, availableViewportWidth),
    availableViewportWidth
  );
  const width = isExpanded ? expandedWidth : minimizedWidth;
  const maxClampedWidth = expandedWidth;
  const headerLeft = clamp(
    position.left,
    ICOMX_VIEWPORT_MARGIN,
    viewport.width - maxClampedWidth - ICOMX_VIEWPORT_MARGIN
  );

  if (!isExpanded) {
    const headerTop = clamp(
      position.top,
      ICOMX_VIEWPORT_MARGIN,
      viewport.height - ICOMX_HEADER_HEIGHT - ICOMX_VIEWPORT_MARGIN
    );

    return {
      headerLeft,
      headerTop,
      panelTop: headerTop,
      width,
      height: ICOMX_HEADER_HEIGHT,
      openDirection,
    };
  }

  const desiredHeight = clamp(
    getViewportUnits(ICOMX_EXPANDED_HEIGHT, viewport.height),
    ICOMX_HEADER_HEIGHT + ICOMX_MIN_EXPANDED_BODY_HEIGHT,
    Math.max(ICOMX_HEADER_HEIGHT, viewport.height - ICOMX_VIEWPORT_MARGIN * 2)
  );
  const headerTop = openDirection === 'down'
    ? clamp(
      position.top,
      ICOMX_VIEWPORT_MARGIN,
      viewport.height - desiredHeight - ICOMX_VIEWPORT_MARGIN
    )
    : clamp(
      position.top,
      desiredHeight - ICOMX_HEADER_HEIGHT + ICOMX_VIEWPORT_MARGIN,
      viewport.height - ICOMX_HEADER_HEIGHT - ICOMX_VIEWPORT_MARGIN
    );
  const height = desiredHeight;
  const panelTop =
    openDirection === 'down' ? headerTop : headerTop + ICOMX_HEADER_HEIGHT - height;

  return {
    headerLeft,
    headerTop,
    panelTop,
    width,
    height,
    openDirection,
  };
}

export function getPreferredOpenDirection(
  position: HeaderPosition,
  viewport: ViewportSize,
  currentDirection: 'up' | 'down'
) {
  const headerTop = clamp(
    position.top,
    ICOMX_VIEWPORT_MARGIN,
    viewport.height - ICOMX_HEADER_HEIGHT - ICOMX_VIEWPORT_MARGIN
  );
  const availableDown = viewport.height - ICOMX_VIEWPORT_MARGIN - headerTop;
  const availableUp = headerTop + ICOMX_HEADER_HEIGHT - ICOMX_VIEWPORT_MARGIN;

  if (currentDirection === 'down') {
    return availableUp > availableDown + ICOMX_DIRECTION_SWITCH_THRESHOLD ? 'up' : 'down';
  }

  return availableDown > availableUp + ICOMX_DIRECTION_SWITCH_THRESHOLD ? 'down' : 'up';
}

function logSecurityRejection(rejection: IcomxSecurityRejection) {
  console.warn('iComX bridge security rejection', rejection.reason, rejection.details);
  Sentry.captureMessage(`iComX bridge security rejection: ${rejection.reason}`);
}

export default function IcomxChatProvider({ children }: IcomxChatProviderProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastBridgeEventKeyRef = useRef<string | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const dragListenersRef = useRef<DragListeners | null>(null);
  const dragFrameRef = useRef<number | null>(null);
  const pendingPositionRef = useRef<HeaderPosition | null>(null);
  const queuedLaunchTimeoutRef = useRef<number | null>(null);
  const suppressToggleUntilRef = useRef(0);
  const finishDraggingRef = useRef<() => void>(() => undefined);
  const { showErrorMessage, showWarningMessage } = useNotiMessage();
  const [isDragging, setIsDragging] = useState(false);
  const [viewportSize, setViewportSize] = useState<ViewportSize>(() => resolveViewportSize());
  const [headerPosition, setHeaderPosition] = useState<HeaderPosition>(() => {
    const viewport = resolveViewportSize();
    const viewportBucket = getViewportBucket(viewport);
    const storedPosition = readStoredHeaderPosition(viewportBucket);

    return storedPosition ?? getDefaultHeaderPosition(viewport);
  });

  const [widgetState, updateWidgetState] = useObjectState<WidgetState>({
    isExpanded: false,
    activeIntent: null,
    pendingContactUuid: null,
    openDirection: 'up',
  });

  const { data: embedConfig } = useIcomxEmbedConfig({
    retry: false,
    staleTime: 0,
  });

  const iframeOrigin = useMemo(
    () => resolveIcomxIframeOrigin(embedConfig?.iframeOrigin, embedConfig?.iframeUrl),
    [embedConfig?.iframeOrigin, embedConfig?.iframeUrl]
  );
  const iframeSrc = useMemo(() => buildIcomxIframeSrc(embedConfig?.iframeUrl), [embedConfig?.iframeUrl]);

  const ensureIcomxResourceLink = useEnsureIcomxResourceLink();

  const onBridgeError = useCallback((error: Error) => {
    console.error('iComX bridge failure', error);
    Sentry.captureException(error);
  }, []);

  const bridge = useIcomxBridge({
    iframeRef,
    iframeOrigin,
    onBridgeError,
    onSecurityRejection: logSecurityRejection,
  });

  const hasIframeConfig = !!iframeSrc && !!iframeOrigin;
  const isEnabled = !!embedConfig?.enabled && hasIframeConfig;
  const isLoading = embedConfig === undefined;
  const viewportBucket = useMemo(() => getViewportBucket(viewportSize), [viewportSize]);
  const widgetLayout = useMemo(
    () => getWidgetLayout(headerPosition, viewportSize, widgetState.isExpanded, widgetState.openDirection),
    [headerPosition, viewportSize, widgetState.isExpanded, widgetState.openDirection]
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportSize(resolveViewportSize());
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setHeaderPosition((currentPosition) => {
      const nextLayout = getWidgetLayout(
        currentPosition,
        viewportSize,
        widgetState.isExpanded,
        widgetState.openDirection
      );

      if (
        nextLayout.headerLeft === currentPosition.left
        && nextLayout.headerTop === currentPosition.top
      ) {
        return currentPosition;
      }

      return {
        left: nextLayout.headerLeft,
        top: nextLayout.headerTop,
      };
    });
  }, [viewportSize, widgetState.isExpanded, widgetState.openDirection]);

  useEffect(() => {
    writeStoredHeaderPosition(headerPosition, viewportBucket);
  }, [headerPosition, viewportBucket]);

  useEffect(() => {
    const storedPosition = readStoredHeaderPosition(viewportBucket);

    setHeaderPosition(storedPosition ?? getDefaultHeaderPosition(viewportSize));
  }, [viewportBucket, viewportSize]);

  useEffect(() => {
    if (!widgetState.isExpanded || isDragging) {
      return;
    }

    const preferredDirection = getPreferredOpenDirection(
      headerPosition,
      viewportSize,
      widgetState.openDirection
    );

    if (preferredDirection !== widgetState.openDirection) {
      updateWidgetState({ openDirection: preferredDirection });
    }
  }, [headerPosition, isDragging, updateWidgetState, viewportSize, widgetState.isExpanded, widgetState.openDirection]);

  useEffect(() => {
    if (!bridge.lastEvent) {
      return;
    }

    const eventKey = `${bridge.lastEvent.requestId}:${bridge.lastEvent.type}`;

    if (lastBridgeEventKeyRef.current === eventKey) {
      return;
    }

    lastBridgeEventKeyRef.current = eventKey;

    if (bridge.lastEvent.type === 'comx.iframe.popup_blocked') {
      showWarningMessage('The iComX sign-in or provider popup was blocked. Allow popups and continue in the new window.');
      return;
    }

    if (bridge.lastEvent.type === 'comx.iframe.popup_closed') {
      showWarningMessage('The iComX sign-in or provider popup was closed before completion. Reopen the flow from iComX to continue.');
    }
  }, [bridge.lastEvent, showWarningMessage]);

  const clearQueuedLaunchTimeout = useCallback(() => {
    if (queuedLaunchTimeoutRef.current === null) {
      return;
    }

    clearTimeout(queuedLaunchTimeoutRef.current);
    queuedLaunchTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    clearQueuedLaunchTimeout();

    if (!widgetState.pendingContactUuid || bridge.isReady || !isEnabled) {
      return;
    }

    queuedLaunchTimeoutRef.current = window.setTimeout(() => {
      queuedLaunchTimeoutRef.current = null;
      showErrorMessage(ICOMX_READY_TIMEOUT_MESSAGE, { persist: false });
      updateWidgetState({ activeIntent: null, pendingContactUuid: null });
    }, ICOMX_READY_TIMEOUT_MS);

    return clearQueuedLaunchTimeout;
  }, [bridge.isReady, clearQueuedLaunchTimeout, isEnabled, showErrorMessage, updateWidgetState, widgetState.pendingContactUuid]);

  const handleIframeLoad = useCallback(() => {
    bridge.refreshContext();
  }, [bridge]);

  const executeOpenLastConversation = useCallback(
    async (contactUuid: string) => {
      if (widgetState.activeIntent) {
        return;
      }

      updateWidgetState({ activeIntent: OPEN_CHAT_CONVERSATION });

      try {
        await bridge.sendCommand(OPEN_CHAT_CONVERSATION, {
          contactUuid,
          fallbackToDirect: true,
        });
      } catch (error) {
        showErrorMessage(getIcomxBridgeErrorMessage(error), { persist: false });
      } finally {
        updateWidgetState({ activeIntent: null, pendingContactUuid: null });
      }
    },
    [bridge, showErrorMessage, updateWidgetState, widgetState.activeIntent]
  );

  useEffect(() => {
    if (!widgetState.pendingContactUuid || widgetState.activeIntent || !isEnabled || !bridge.isReady) {
      return;
    }

    void executeOpenLastConversation(widgetState.pendingContactUuid);
  }, [bridge.isReady, executeOpenLastConversation, isEnabled, widgetState.activeIntent, widgetState.pendingContactUuid]);

  const openResourceConversation = useCallback(
    async (resource: IcomxResourceDescriptor) => {
      if (!isEnabled || widgetState.activeIntent || widgetState.pendingContactUuid) {
        return;
      }

      updateWidgetState({
        isExpanded: true,
        activeIntent: OPEN_CHAT_CONVERSATION,
        openDirection: getPreferredOpenDirection(
          headerPosition,
          viewportSize,
          widgetState.openDirection
        ),
      });

      try {
        const link = await ensureIcomxResourceLink.mutateAsync(resource);

        if (!link.comxContactUuid) {
          showErrorMessage(MISSING_CONTACT_UUID_MESSAGE, { persist: false });
          updateWidgetState({ activeIntent: null, pendingContactUuid: null });
          return;
        }

        if (bridge.isReady) {
          await bridge.sendCommand(OPEN_CHAT_CONVERSATION, {
            contactUuid: link.comxContactUuid,
            fallbackToDirect: true,
          });

          updateWidgetState({ activeIntent: null, pendingContactUuid: null });
          return;
        }

        updateWidgetState({
          activeIntent: null,
          pendingContactUuid: link.comxContactUuid,
        });
      } catch (error) {
        const message = ensureIcomxResourceLink.isError
          ? getIcomxApiErrorMessage(error, `Unable to prepare the ${resource.resourceType.toLowerCase()} chat mapping`)
          : getIcomxBridgeErrorMessage(error);

        showErrorMessage(message, { persist: false });
        updateWidgetState({ activeIntent: null, pendingContactUuid: null });
      }
    },
    [
      bridge,
      ensureIcomxResourceLink,
      isEnabled,
      showErrorMessage,
      updateWidgetState,
      widgetState.activeIntent,
      widgetState.openDirection,
      widgetState.pendingContactUuid,
    ]
  );

  const toggleExpanded = useCallback(() => {
    updateWidgetState({
      isExpanded: !widgetState.isExpanded,
      openDirection: widgetState.isExpanded
        ? widgetState.openDirection
        : getPreferredOpenDirection(headerPosition, viewportSize, widgetState.openDirection),
    });
  }, [headerPosition, updateWidgetState, viewportSize, widgetState.isExpanded, widgetState.openDirection]);

  const commitDraggedPosition = useCallback((nextPosition: HeaderPosition) => {
    setHeaderPosition(nextPosition);
    pendingPositionRef.current = null;
    dragFrameRef.current = null;
  }, []);

  const scheduleDraggedPosition = useCallback((nextPosition: HeaderPosition) => {
    pendingPositionRef.current = nextPosition;

    if (dragFrameRef.current !== null) {
      return;
    }

    dragFrameRef.current = window.requestAnimationFrame(() => {
      if (pendingPositionRef.current) {
        commitDraggedPosition(pendingPositionRef.current);
      }
    });
  }, [commitDraggedPosition]);

  const finishDragging = useCallback(() => {
    const didMove = !!dragStateRef.current?.moved;
    const finalPosition = pendingPositionRef.current ?? headerPosition;

    if (dragFrameRef.current !== null) {
      window.cancelAnimationFrame(dragFrameRef.current);
      dragFrameRef.current = null;
    }

    if (pendingPositionRef.current) {
      setHeaderPosition(pendingPositionRef.current);
      pendingPositionRef.current = null;
    }

    if (dragListenersRef.current) {
      window.removeEventListener('pointermove', dragListenersRef.current.move);
      window.removeEventListener('pointerup', dragListenersRef.current.up);
      window.removeEventListener('pointercancel', dragListenersRef.current.cancel);
      dragListenersRef.current = null;
    }

    dragStateRef.current = null;
    setIsDragging(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';

    if (didMove && widgetState.isExpanded) {
      updateWidgetState({
        openDirection: getPreferredOpenDirection(finalPosition, resolveViewportSize(), widgetState.openDirection),
      });
    }

    if (didMove) {
      suppressToggleUntilRef.current = Date.now() + ICOMX_DRAG_CLICK_SUPPRESSION_MS;
    }
  }, [headerPosition, updateWidgetState, widgetState.isExpanded, widgetState.openDirection]);

  finishDraggingRef.current = finishDragging;

  const handleWindowPointerMove = useCallback(
    (event: PointerEvent) => {
      const dragState = dragStateRef.current;

      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      event.preventDefault();

      const deltaX = event.clientX - dragState.originX;
      const deltaY = event.clientY - dragState.originY;
      const nextPosition = {
        left: dragState.originLeft + deltaX,
        top: dragState.originTop + deltaY,
      };
      const nextLayout = getWidgetLayout(
        nextPosition,
        resolveViewportSize(),
        widgetState.isExpanded,
        widgetState.openDirection
      );

      if (!dragState.moved && (Math.abs(deltaX) > ICOMX_DRAG_THRESHOLD || Math.abs(deltaY) > ICOMX_DRAG_THRESHOLD)) {
        dragStateRef.current = {
          ...dragState,
          moved: true,
        };
        setIsDragging(true);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      }

      scheduleDraggedPosition({
        left: nextLayout.headerLeft,
        top: nextLayout.headerTop,
      });
    },
    [scheduleDraggedPosition, widgetState.isExpanded, widgetState.openDirection]
  );

  const handleDragHandlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();

      dragStateRef.current = {
        pointerId: event.pointerId,
        originLeft: widgetLayout.headerLeft,
        originTop: widgetLayout.headerTop,
        originX: event.clientX,
        originY: event.clientY,
        moved: false,
      };

      const moveListener = (nativeEvent: PointerEvent) => {
        handleWindowPointerMove(nativeEvent);
      };
      const upListener = (nativeEvent: PointerEvent) => {
        if (dragStateRef.current?.pointerId !== nativeEvent.pointerId) {
          return;
        }

        finishDraggingRef.current();
      };
      const cancelListener = (nativeEvent: PointerEvent) => {
        if (dragStateRef.current?.pointerId !== nativeEvent.pointerId) {
          return;
        }

        finishDraggingRef.current();
      };

      dragListenersRef.current = {
        move: moveListener,
        up: upListener,
        cancel: cancelListener,
      };

      window.addEventListener('pointermove', moveListener, { passive: false });
      window.addEventListener('pointerup', upListener);
      window.addEventListener('pointercancel', cancelListener);
    },
    [handleWindowPointerMove, widgetLayout.headerLeft, widgetLayout.headerTop]
  );

  const handleDragHandleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleHeaderClick = useCallback(() => {
    if (Date.now() < suppressToggleUntilRef.current) {
      return;
    }

    toggleExpanded();
  }, [toggleExpanded]);

  const handleHeaderKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      toggleExpanded();
    },
    [toggleExpanded]
  );

  useEffect(() => () => {
    clearQueuedLaunchTimeout();
    finishDraggingRef.current();
  }, [clearQueuedLaunchTimeout]);

  const contextValue = useMemo(
    () => ({
      isEnabled,
      isLoading,
      isLaunching: !!widgetState.activeIntent || !!widgetState.pendingContactUuid,
      openResourceConversation,
    }),
    [isEnabled, isLoading, openResourceConversation, widgetState.activeIntent, widgetState.pendingContactUuid]
  );

  return (
    <IcomxChatContext.Provider value={contextValue}>
      {children}

      {isEnabled && (
        <Paper
          data-testid="icomx-global-widget"
          data-open-direction={widgetLayout.openDirection}
          elevation={isDragging ? 16 : widgetState.isExpanded ? 12 : 8}
          style={{
            width: `${widgetLayout.width}px`,
            height: `${widgetLayout.height}px`,
            transform: `translate3d(${widgetLayout.headerLeft}px, ${widgetLayout.panelTop}px, 0)`,
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 2000,
            display: 'flex',
            flexDirection: widgetState.isExpanded && widgetLayout.openDirection === 'up' ? 'column-reverse' : 'column',
            overflow: 'hidden',
            borderRadius: 2,
            willChange: isDragging ? 'transform' : 'auto',
            transition: isDragging
              ? 'box-shadow 120ms ease'
              : 'transform 140ms ease, width 140ms ease, height 140ms ease, box-shadow 140ms ease',
          }}
        >
          <Box
            data-testid="icomx-global-widget-header"
            data-dragging={isDragging ? 'true' : 'false'}
            role="button"
            tabIndex={0}
            onClick={handleHeaderClick}
            onKeyDown={handleHeaderKeyDown}
            aria-label={widgetState.isExpanded ? 'Minimize iComX panel' : 'Expand iComX panel'}
            aria-expanded={widgetState.isExpanded}
            sx={{
              minHeight: ICOMX_HEADER_HEIGHT,
              px: 2,
              py: 1.25,
              color: 'common.white',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              touchAction: 'none',
              boxShadow: isDragging ? 'inset 0 0 0 1px rgba(255,255,255,0.28)' : 'none',
              filter: isDragging ? 'brightness(0.96)' : 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                data-testid="icomx-global-widget-drag-handle"
                onPointerDown={handleDragHandlePointerDown}
                onClick={handleDragHandleClick}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  mr: 0.25,
                }}
              >
                <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.72 }} />
              </Box>
              <ChatBubbleOutlineIcon fontSize="small" />
              <Typography variant="subtitle2" color="inherit">
                iComx
              </Typography>
            </Box>

            {widgetState.isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowUpIcon fontSize="small" />}
          </Box>

          <Divider />

          <Box
            data-testid="icomx-global-panel-body"
            sx={{
              flex: 1,
              display: widgetState.isExpanded ? 'flex' : 'none',
              flexDirection: 'column',
              minHeight: 0,
              backgroundColor: '#f4f6f8',
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <iframe
                ref={iframeRef}
                title="iComX chat"
                src={iframeSrc!}
                onLoad={handleIframeLoad}
                sandbox={ICOMX_IFRAME_SANDBOX}
                allow="clipboard-read; clipboard-write"
                referrerPolicy="strict-origin-when-cross-origin"
                style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
              />
            </Box>
          </Box>
        </Paper>
      )}
    </IcomxChatContext.Provider>
  );
}